import { get, send } from "./api.js";

export type CreditReviewScope = "mine" | "organisation";
export type CreditReviewSeverity = "action" | "warning";
export type CreditReviewStatus = "open" | "resolved" | "dismissed";

export interface CreditReviewItem {
  id: string;
  kind: string;
  status: CreditReviewStatus;
  severity: CreditReviewSeverity;
  provider_id: number | null;
  portal_student_id: string | null;
  lesson_block_id: string | null;
  amelia_appointment_id: number | null;
  amelia_booking_id: number | null;
  amelia_payment_id: number | null;
  message: string;
  detail: Record<string, unknown>;
  first_seen_at: string;
  last_seen_at: string;
  available_actions: string[];
  student_name?: string | null;
  lesson_starts_at?: string | null;
}

export interface CreditReviewList {
  total: number;
  items: CreditReviewItem[];
}

export interface PurchaseClaim {
  id: string;
  claim_payer_email: string | null;
  claim_purchase_student_name: string | null;
  claim_invite_status: string | null;
  claim_invite_attempts: number;
  claim_invite_error: string | null;
  credits_purchased: number;
  paid_at: string;
  expires_at: string;
  created_at?: string | null;
  amelia_purchase_appointment_id?: number | null;
  amelia_purchase_booking_id?: number | null;
  amelia_payment_id?: number | null;
  payment_amount?: string | null;
  payment_currency?: string | null;
  claim_invite_next_at?: string | null;
  claim_recovery_status?: string | null;
  claim_recovery_error?: string | null;
  claim_preference_status?: string | null;
  claim_preference_reason?: string | null;
  claim_preference_checked_at?: string | null;
  payment_reversal_hold_at?: string | null;
  payment_reversal_cleared_at?: string | null;
}

export interface MailEvidence {
  status: "queued" | "sending" | "sent" | "failed" | "delivered" | "bounced" | "complained";
  attempts: number;
  sentAt: string | null;
  updatedAt: string;
  hasProviderId: boolean;
  lastError: string | null;
}

export interface PurchaseClaimDelivery {
  paymentId: number;
  claim: null | {
    bookingId: number;
    createdAt: string;
    verifiedAt: string | null;
    selectedAt: string | null;
    deliveryStartedAt: string | null;
    invitation: null | {
      createdAt: string;
      expiresAt: string;
      mailQueuedAt: string | null;
      acceptedAt: string | null;
      revokedAt: string | null;
    };
    invitationMail: MailEvidence | null;
    actionMail: MailEvidence | null;
  };
}

export interface FailedCreditMail {
  id: string;
  toEmail: string;
  template: string;
  status: "failed" | "bounced" | "complained";
  studentId: string | null;
  updatedAt: string;
}

export interface CreditCutoverStatus {
  evaluated_at: string;
  cohort_state: "disabled" | "scheduled" | "active" | "stopped";
  cohort_start_at: string | null;
  cohort_stop_at: string | null;
  coupon_authority_boundary_at: string | null;
  counts: {
    local_purchase_cohort_blocks: number;
    coupon_authority_purchase_blocks: number;
    /** Open reviews for paid purchases still waiting for a Hub student. */
    purchase_identity_holds: number;
    /** Historical website blocks held because a legacy coupon cannot be ruled out. Absent from older WMF deployments. */
    legacy_provenance_holds?: number;
    claim_required_purchases: number;
    claim_invite_failures: number;
    credit_ready_delivery_failures: number;
    local_credit_reservations: number;
    booking_reconciliation_cases: number;
    cancellation_restore_reviews: number;
    refund_chargeback_reviews: number;
    payment_reversal_holds: number;
    migration_held_blocks: number;
    legacy_coupon_conversions: number;
    legacy_conversion_reviews: number;
    stale_workers: number;
    failed_workers: number;
  };
}

export type CancellationCreditResolution = "restore" | "consume";

export interface PaidCancellationCreditRepairResult {
  appointment_id: number;
  booking_id: number;
  lesson_block_id: string;
  grant_transaction_id: string;
  replayed: boolean;
}

export interface CancellationCreditDecisionResult {
  appointment_id: number;
  credit_resolution: CancellationCreditResolution;
  credits_restored: number;
  credit_review_required: boolean;
}

interface CreditReviewContext {
  students: Array<{ id: string; fullName: string }>;
  appointments: Array<{
    ameliaAppointmentId: number;
    startsAt: string;
    studentId: string | null;
    studentName: string | null;
  }>;
}

async function listWithDisplayContext(
  scope: CreditReviewScope,
  page: number,
  limit: number,
): Promise<CreditReviewList> {
  const review = await get<CreditReviewList>("/staff-api/api/v1/credit-review-items", {
    scope,
    status: "open",
    limit: String(limit),
    offset: String(Math.max(0, page - 1) * limit),
  });

  if (review.items.length === 0) return review;

  const studentIds = [
    ...new Set(
      review.items
        .map((item) => item.portal_student_id)
        .filter((id): id is string => id !== null),
    ),
  ];
  const ameliaAppointmentIds = [
    ...new Set(
      review.items
        .map((item) => item.amelia_appointment_id)
        .filter((id): id is number => id !== null),
    ),
  ];

  try {
    const context = await send<CreditReviewContext>(
      "POST",
      "/admin/credit-review/context",
      { studentIds, ameliaAppointmentIds },
    );
    const studentNames = new Map(context.students.map((student) => [student.id, student.fullName]));
    const appointments = new Map(
      context.appointments.map((appointment) => [appointment.ameliaAppointmentId, appointment]),
    );

    return {
      ...review,
      items: review.items.map((item) => {
        const appointment =
          item.amelia_appointment_id === null
            ? undefined
            : appointments.get(item.amelia_appointment_id);
        const directStudentName =
          item.portal_student_id === null ? undefined : studentNames.get(item.portal_student_id);
        const appointmentStudentName =
          appointment &&
          (item.portal_student_id === null || appointment.studentId === item.portal_student_id)
            ? appointment.studentName
            : null;
        return {
          ...item,
          student_name: directStudentName ?? appointmentStudentName ?? null,
          lesson_starts_at: appointment?.startsAt ?? null,
        };
      }),
    };
  } catch {
    // Display enrichment must never suppress the financial queue itself. If
    // the Hub mirror is temporarily unavailable, keep the WMF review rows and
    // omit human context rather than falling back to raw internal identifiers.
    return {
      ...review,
      items: review.items.map((item) => ({
        ...item,
        student_name: null,
        lesson_starts_at: null,
      })),
    };
  }
}

export const creditReviewApi = {
  list: listWithDisplayContext,

  pendingPurchaseClaims: (): Promise<PurchaseClaim[]> =>
    get<PurchaseClaim[]>("/staff-api/api/v1/lesson-blocks/purchase-claims", { limit: "100" }),

  purchaseClaimDelivery: (paymentIds: number[]): Promise<{ items: PurchaseClaimDelivery[] }> =>
    send("POST", "/admin/purchase-claims/delivery", { paymentIds }),

  failedCreditMails: (): Promise<{ total: number; items: FailedCreditMail[] }> =>
    get<{ total: number; items: FailedCreditMail[] }>("/admin/credit-mails/failed"),

  cutoverStatus: (): Promise<CreditCutoverStatus> =>
    get<CreditCutoverStatus>("/staff-api/api/v1/reports/credit-cutover-status"),

  resendPurchaseClaim: (blockId: string): Promise<PurchaseClaim> =>
    send<PurchaseClaim>("POST", `/staff-api/api/v1/lesson-blocks/purchase-claims/${encodeURIComponent(blockId)}/resend`, {}),

  activatePurchaseClaim: (
    blockId: string,
    studentId: string,
    method: "verified_claim" | "staff_assignment",
    reason: string,
  ): Promise<unknown> =>
    send("POST", `/staff-api/api/v1/lesson-blocks/purchase-claims/${encodeURIComponent(blockId)}/activate`, {
      portal_student_id: studentId,
      method,
      reason,
    }),

  clearPaymentReversal: (blockId: string, reason: string): Promise<unknown> =>
    send("POST", `/staff-api/api/v1/lesson-blocks/${encodeURIComponent(blockId)}/payment-reversal/clear`, { reason }),

  decideCancellation: (
    appointmentId: number,
    creditResolution: CancellationCreditResolution,
    idempotencyKey: string,
    note?: string,
  ): Promise<CancellationCreditDecisionResult> =>
    send<CancellationCreditDecisionResult>(
      "POST",
      `/staff-api/api/v1/appointments/${appointmentId}/cancellation-credit-decision`,
      {
        credit_resolution: creditResolution,
        ...(note ? { note } : {}),
      },
      { "Idempotency-Key": idempotencyKey },
    ),

  /**
   * Issue the one rebooking credit this cancelled paid lesson was already due.
   * FastAPI finds the recorded decision itself and never issues a second one,
   * so a retry is safe without a client key.
   */
  repairPaidCancellationCredit: (
    appointmentId: number,
  ): Promise<PaidCancellationCreditRepairResult> =>
    send<PaidCancellationCreditRepairResult>(
      "POST",
      `/staff-api/api/v1/appointments/${appointmentId}/paid-cancellation-credit/repair`,
      {},
    ),

  dismiss: (
    reviewItemId: string,
    note: string,
    idempotencyKey: string,
  ): Promise<CreditReviewItem> =>
    send<CreditReviewItem>(
      "POST",
      `/staff-api/api/v1/credit-review-items/${encodeURIComponent(reviewItemId)}/dismiss`,
      { note },
      { "Idempotency-Key": idempotencyKey },
    ),
};
